import { Ng2StateDeclaration } from '@uirouter/angular';
import { Param, StateService, Transition, UIRouter } from '@uirouter/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { Accessibility, Breadcrumbs, CimaAuthGuidService, CimaCore, CimaStepUpErrorService, CimaUrl, TrialService, XmStore, XmStoreUtil } from 'core/services';
import { StoreAction } from 'store/actions';
import { CmsCore } from 'store/cms/models';
import { AuthLogout } from 'src/xm/route-guards';
import { ParamType, PASSIVE_OAUTH_BLACKLIST, TransitionHooks } from 'core/constants';

import { BaseComponent } from 'src/xm/base/base';
import { XmEmptyComponent } from 'shared/empty/empty';

export function AppInit(
    cimaCore: CimaCore,
    accessibility: Accessibility,
    breadcrumb: Breadcrumbs,
    state: StateService,
    transition: Transition,
    trialService: TrialService,
    cimsAuthGuidService: CimaAuthGuidService,
    cimaStepUpErrorService: CimaStepUpErrorService): Promise<string> {

    breadcrumb.init();
    accessibility.init();
    trialService.init(transition.params().TRIAL_ID);
    cimaStepUpErrorService.init(transition.params().error);
    cimsAuthGuidService.init(transition);

    return cimaCore.loadCimaToken().then((token: string) => {

        const initPassiveOauth: boolean = !PASSIVE_OAUTH_BLACKLIST.some((stateRegex: RegExp) => stateRegex.test(transition.to().name));

        if (initPassiveOauth) {
            cimaCore.tryPassiveAuthLogin();
        }

        return token;
    })
        .catch(() => {
            // if we cannot get a token, then go to downtime
            state.go('downtime');

            return Promise.reject('no token');
        });
}

export function LoadCmsCore(xmStore: XmStore): Promise<CmsCore> {
    return XmStoreUtil.defaultCatch(xmStore.fetchPromise<CmsCore>(StoreAction.GET_CORE));
}

export function transitionStart(uiRouter: UIRouter): Observable<Transition> {
    return uiRouter.globals.start$.pipe(
        map((transition: Transition) => transition),
        distinctUntilChanged()
    );
}

export function transitionSuccess(uiRouter: UIRouter): Observable<Transition> {
    return uiRouter.globals.success$.pipe(
        map((transition: Transition) => transition),
        distinctUntilChanged()
    );
}

export function paramsChanged(uiRouter: UIRouter): Observable<Param> {
    return uiRouter.globals.params$.pipe(
        map((params: Param) => params),
        distinctUntilChanged()
    );
}

export const XM_ROUTES: Ng2StateDeclaration[] = [
    {
        abstract: true,
        name: 'xm',
        url: '?TRIAL_ID&AdobeNoBueno&cbmya_sso&INTCMP&CMP&cmp&intcmp&acc&error',
        resolve: [
            {
                token: 'appInit',
                deps: [ CimaCore, Accessibility, Breadcrumbs, StateService, Transition, TrialService, CimaAuthGuidService, CimaStepUpErrorService ],
                resolveFn: AppInit
            },
            {
                token: 'pageDataCore',
                deps: [ XmStore, 'appInit' ],
                resolveFn: LoadCmsCore
            },
            {
                token: TransitionHooks.START,
                deps: [ UIRouter ],
                resolveFn: transitionStart
            },
            {
                token: TransitionHooks.SUCCESS,
                deps: [ UIRouter ],
                resolveFn: transitionSuccess
            },
            {
                token: TransitionHooks.PARAMS,
                deps: [ UIRouter ],
                resolveFn: paramsChanged
            }
        ],
        views: {
            '': {
                component: BaseComponent
            },
            'nav@xm': {
                component: XmEmptyComponent
            },
            'footer@xm': {
                component: XmEmptyComponent
            }
        },
        params: {
            TRIAL_ID: {
                type: ParamType.encoded
            },
            cbmya_sso: {
                type: ParamType.query
            },
            acc: {
                type: ParamType.query
            },
            error: {
                type: ParamType.query
            }
        }
    },
    {
        name: 'esim-transfer.**',
        url: '/',
        loadChildren: () => import('./esim-transfer/module').then((m: typeof import('./esim-transfer/module')) => m.EsimTransferModule)
    },
    {
        name: 'logout',
        url: '/logout',
        resolve: [
            {
                token: 'cimaLogout',
                deps: [ CimaUrl ],
                resolveFn: AuthLogout
            }
        ]
    },
    {
        name: 'error.**',
        url: '/error',
        loadChildren: () => import('./error/error/module').then((m: typeof import('./error/error/module')) => m.ErrorModule)
    },
    {
        name: 'downtime.**',
        url: '/downtime',
        loadChildren: () => import('./error/downtime/module').then((m: typeof import('./error/downtime/module')) => m.DowntimeModule)
    },
    {
        name: 'non-xfinity-customer.**',
        url: '/add-xfinity-mobile',
        loadChildren: () => import('./error/non-xfinity-customer/module').then((m: typeof import('./error/non-xfinity-customer/module')) => m.NonCustomerModule)
    },
    {
        name: '404.**',
        loadChildren: () => import('./error/404/module').then((m: typeof import('./error/404/module')) => m.PageNotFoundModule)
    },
    {
        name: 'activation-error.**',
        url: '/activate-error',
        loadChildren: () => import('./error/activation-error/module').then((m: typeof import('./error/activation-error/module')) => m.ActivationErrorComponent)
    },
    {
        name: 'account-error.**',
        url: '/account-error',
        loadChildren: () => import('./error/account-error/module').then((m: typeof import('./error/account-error/module')) => m.AccountErrorModule)
    },
    {
        name: 'account-upgrade-error.**',
        url: '/account-upgrade-error',
        loadChildren: () => import('./error/account-upgrade-error/module').then((m: typeof import('./error/account-upgrade-error/module')) => m.AccountUpgradeErrorModule)
    }
];